<template>
  <div class="c-main-nav" :class="isOpen ? 'c-main-nav--overlay' : ''">
    <div v-click-outside="close">
      <div
        class="c-main-nav__top-button-wrapper"
        :class="{ 'c-main-nav__top-button-wrapper--nav-open': isOpen }"
      >
        <a :href="searchUrl" class="c-main-nav__top-search-button">
          <span
            aria-hidden="true"
            class="c-icon c-icon--s c-main-nav__top-search-icon"
          >
            <svg>
              <use xlink:href="#icon-search"></use>
            </svg>
          </span>
          <span class="c-main-nav__top-button-label">{{
            this.translate.search
          }}</span>
        </a>
        <button
          @click="open"
          class="c-main-nav__toggle"
          :aria-expanded="isOpen ? 'true' : 'false'"
          :style="[isOpen ? { display: 'none' } : { display: 'flex' }]"
          aria-controls="main-nav-content"
          @keydown.esc="close"
        >
          <span class="t-visually-hidden" data-js="nav-text"
            >Öppna {{ this.translate.menu }}</span
          >
          <span class="c-main-nav__nav-burger" aria-hidden="true">
            <span class="c-main-nav__nav-bar"> </span>
            <span class="c-main-nav__nav-bar"> </span>
            <span class="c-main-nav__nav-bar"> </span>
          </span>
          <span class="c-main-nav__top-button-label">{{
            this.translate.menu
          }}</span>
        </button>
        <button
          @click="close"
          :style="[!isOpen ? { display: 'none' } : { display: 'flex' }]"
          class="c-main-nav__toggle"
          :aria-expanded="isOpen ? 'true' : 'false'"
          aria-controls="main-nav-content"
        >
          <span class="t-visually-hidden" data-js="nav-text"
            >{{ this.translate.close }} {{ this.translate.menu }}</span
          >

          <span aria-hidden="true" class="c-icon c-icon--s">
            <svg>
              <use xlink:href="#icon-close"></use>
            </svg>
          </span>
        </button>
      </div>

      <transition name="toggle">
        <div
          id="main-nav-content"
          class="c-main-nav__content"
          v-show="isOpen"
          ref="content"
        >
          <div class="c-main-nav">
            <div class="c-main-nav__sticky-top">
              <div class="c-main-nav__top-container">
                <span class="c-main-nav__top-continer-heading">
                  {{ this.translate.menu }}
                </span>
                <button class="c-main-nav__close" @click="close">
                  <span class="c-main-nav__close-label">{{
                    this.translate.close
                  }}</span>
                  <span aria-hidden="true" class="c-icon c-icon--xs">
                    <svg>
                      <use xlink:href="#icon-close"></use>
                    </svg>
                  </span>
                </button>
              </div>
            </div>
            <slot />
            <div class="locale-selector c-main-nav__locale-selector">
              <div class="locale-selector__locale-region">
                <span class="t-visually-hidden">Byt textspråk</span>
                <svg class="c-icon c-icon--xs" aria-hidden="true">
                  <use xlink:href="#icon-translate"></use>
                </svg>

                <div class="locale-selector__region-list">
                  <template v-for="(item, index) in languagesList">
                    <span
                      v-if="
                        item.Attributes && item.Attributes.selected === 'true'
                      "
                      :key="'active-' + index"
                      class="locale-selector__language--active"
                      >{{ item.Title }}</span
                    >
                    <a v-else :key="'link-' + index" :href="item.Href">{{
                      item.Title
                    }}</a>
                    <span
                      v-if="index < languagesList.length - 1"
                      :key="'slash-' + index"
                      class="locale-selector__language-link-slash"
                      >|</span
                    >
                  </template>
                </div>
              </div>
              <div class="locale-selector__locale-language">
                <svg class="c-icon c-icon--xs" aria-hidden="true">
                  <use xlink:href="#icon-globe"></use>
                </svg>

                <div class="locale-selector__language-list">
                  <template v-for="(item, index) in siteLanguageList">
                    <span
                      v-if="
                        item.Attributes && item.Attributes.selected === 'true'
                      "
                      :key="'active-' + index"
                      class="locale-selector__language--active"
                      >{{ item.Text }}</span
                    >
                    <a
                      v-else
                      :key="'link-' + index"
                      :href="item.Href"
                      :target="item.Target"
                      >{{ item.Text }}</a
                    >
                    <span
                      v-if="index < siteLanguageList.length - 1"
                      :key="'slash-' + index"
                      class="locale-selector__language-link-slash"
                      >|</span
                    >
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import vClickOutside from "v-click-outside";
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from "body-scroll-lock-upgrade";

export default {
  name: "VMainNav",
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    sitelanguages: {
      type: Array,
    },
    languages: {
      type: Array,
    },
    searchUrl: {
      type: String,
      default: "/sok/",
    },
    translationString: {
      type: [Object, String],
      default: () => ({}),
    },
  },
  data() {
    return {
      isOpen: false,
      siteLanguageList: this.sitelanguages,
      languagesList: this.languages,
      translate: {},
    };
  },
  beforeDestroy() {
    clearAllBodyScrollLocks();
  },
  methods: {
    open() {
      disableBodyScroll(this.$refs.content);
      this.isOpen = true;

      this.focusFirst();
    },
    close(e) {
      if (e.target.hasAttribute("data-open-popup")) {
        return;
      }
      enableBodyScroll(this.$refs.content);
      this.isOpen = this.isOpen && false;
    },
    focusFirst() {
      const firstFocusable = this.$refs.content.querySelector(
        "a, button, input, textarea, select, details, [tabindex]:not([tabindex='-1'])",
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    },
  },
  watch: {
    isOpen() {
      document.querySelector("html").style.overflow = this.isOpen
        ? "hidden"
        : "auto";
      document.querySelector(".cookieconsent-link").style.display = this.isOpen
        ? "none"
        : "block";
    },
  },
  created() {
    // Handle translationString as either Object or JSON string
    if (typeof this.translationString === "string") {
      try {
        this.translate = JSON.parse(this.translationString);
      } catch (e) {
        this.translate = {};
      }
    } else {
      this.translate = this.translationString || {};
    }
  },
};
</script>
